(function () {
  'use strict';

  const PIECES = ['table', 'chair1', 'chair2'];
  const KNOWN_COLOURS = [
    'watermelon-sugar',
    'fairy-bread',
    'fairy-floss',
    'blueberry',
    'lilac',
    'sage',
    'peach',
    'sand',
    'ice',
    'mint',
    'fruit-tingle',
    'sour-apple',
    'blue-fizz',
    'grape-soda',
    'orange-sherbet'
  ];

  function normaliseColour(value) {
    const normalised = String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return KNOWN_COLOURS.find(colour => normalised.includes(colour)) || normalised;
  }

  class JBConfettiColourStudio {
    constructor(root) {
      this.root = root;
      this.activePiece = 'table';
      this.view = 'set';
      this.productName = root.dataset.productName || 'Confetti';
      this.selectorFamily = root.dataset.selectorFamily || 'confetti';
      this.initialSelections = {
        table: root.dataset.initialTable || 'fairy-bread',
        chair1: root.dataset.initialChair1 || 'sand',
        chair2: root.dataset.initialChair2 || 'lilac'
      };
      this.selections = { ...this.initialSelections };
      this.builderRoot = null;
      this.builderData = null;

      this.colourButtons = Array.from(root.querySelectorAll('[data-studio-colour]'));
      this.colours = this.colourButtons.reduce((map, button) => {
        map[button.dataset.studioColour] = {
          key: button.dataset.studioColour,
          label: button.dataset.label,
          hex: button.dataset.hex,
          tableImage: button.dataset.tableImage,
          chairImage: button.dataset.chairImage
        };
        return map;
      }, {});

      this.pieceButtons = Array.from(root.querySelectorAll('[data-studio-piece]'));
      this.stagePieces = Array.from(root.querySelectorAll('[data-stage-piece]'));
      this.viewButtons = Array.from(root.querySelectorAll('[data-studio-view]'));
      this.detailModal = root.querySelector('[data-detail-modal]');
      this.detailClose = root.querySelector('[data-detail-close]');
      this.controlHeading = root.querySelector('[data-control-heading]');
      this.useButton = root.querySelector('[data-use-combination]');
      this.status = root.querySelector('[data-studio-status]');

      this.bindEvents();
      this.update();
      this.connectToBundleBuilder();
    }

    bindEvents() {
      this.pieceButtons.forEach(button => {
        button.addEventListener('click', () => this.setActivePiece(button.dataset.studioPiece));
      });

      this.stagePieces.forEach(button => {
        button.addEventListener('click', () => this.setActivePiece(button.dataset.stagePiece));
      });

      this.colourButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.selections[this.activePiece] = button.dataset.studioColour;
          this.announce(`${button.dataset.label} selected for ${this.getPieceLabel(this.activePiece)}`);
          this.update();
        });
      });

      this.viewButtons.forEach(button => {
        button.addEventListener('click', () => {
          if (button.dataset.studioView === 'detail') {
            this.openDetail(button);
          } else {
            this.closeDetail();
          }
        });
      });

      if (this.detailClose) {
        this.detailClose.addEventListener('click', () => this.closeDetail());
      }

      if (this.detailModal) {
        this.detailModal.addEventListener('click', event => {
          if (event.target === this.detailModal) this.closeDetail();
        });
        this.detailModal.addEventListener('keydown', event => {
          if (event.key === 'Escape') this.closeDetail();
        });
      }

      if (this.useButton) {
        this.useButton.addEventListener('click', () => this.addCombinationToCart());
      }
    }

    connectToBundleBuilder(attempt = 0) {
      const builderRoot = document.querySelector(`.jb-bundle[data-selector-family="${this.selectorFamily}"]`);
      const hasChoices = builderRoot && builderRoot.querySelector('.jb-swatch[data-color-key]');

      if (hasChoices) {
        this.builderRoot = builderRoot;
        this.builderData = window.JBBundleData && window.JBBundleData[builderRoot.dataset.sectionId];
        this.update();
        return;
      }

      if (attempt < 50) {
        window.setTimeout(() => this.connectToBundleBuilder(attempt + 1), 100);
      } else {
        this.announce('The purchasing choices could not be connected. Please refresh the page.');
      }
    }

    setActivePiece(piece) {
      if (!PIECES.includes(piece)) return;
      this.activePiece = piece;
      this.update();
    }

    openDetail(trigger) {
      if (!this.detailModal) return;
      this.detailTrigger = trigger || null;
      this.view = 'detail';
      this.update();
      this.detailModal.classList.add('is-open');
      this.detailModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('jb-colour-studio-modal-open');
      if (this.detailClose) this.detailClose.focus();
    }

    closeDetail() {
      if (!this.detailModal) return;
      const trigger = this.detailTrigger;
      this.view = 'set';
      this.detailModal.classList.remove('is-open');
      this.detailModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jb-colour-studio-modal-open');
      this.update();
      if (trigger && document.contains(trigger)) trigger.focus();
      this.detailTrigger = null;
    }

    getPieceLabel(piece) {
      if (piece === 'table') return 'the table';
      return piece === 'chair1' ? 'chair 1' : 'chair 2';
    }

    getPieceTitle(piece) {
      if (piece === 'table') return 'Table';
      return piece === 'chair1' ? 'Chair 1' : 'Chair 2';
    }

    getColour(key) {
      return this.colours[key] || this.colours[this.initialSelections.table] || Object.values(this.colours)[0];
    }

    getImage(piece, colourKey) {
      const colour = this.getColour(colourKey);
      return piece === 'table' ? colour.tableImage : colour.chairImage;
    }

    getBuilderChoice(piece, colourKey, availableOnly = false) {
      if (!this.builderRoot) return null;
      const buttons = Array.from(
        this.builderRoot.querySelectorAll(`[data-selector="${piece}"] .jb-swatch[data-color-key]`)
      ).filter(button => normaliseColour(button.dataset.colorKey) === colourKey);
      if (!buttons.length) return null;

      const available = buttons.filter(button => !button.disabled && button.dataset.available === 'true');
      if (available.length) return available[available.length - 1];
      return availableOnly ? null : buttons[buttons.length - 1];
    }

    isAvailable(piece, colourKey) {
      if (!this.builderRoot) return false;
      return Boolean(this.getBuilderChoice(piece, colourKey, true));
    }

    getProduct(piece, colourKey) {
      if (!this.builderData) return null;
      const products = piece === 'table' ? this.builderData.tables : this.builderData.chairs;
      const matches = (products || []).filter(product => {
        return normaliseColour(product.colorName || product.title) === colourKey;
      });
      if (!matches.length) return null;
      const available = matches.filter(product => product.available === true && product.status !== 'out_of_stock');
      return (available.length ? available : matches)[(available.length ? available : matches).length - 1];
    }

    update() {
      const colourWord = this.root.dataset.colourWord || 'colour';
      const activeColour = this.getColour(this.selections[this.activePiece]);

      this.root.classList.toggle('is-detail-view', this.view === 'detail');
      this.root.style.setProperty('--jb-active-colour', activeColour.hex);

      this.viewButtons.forEach(button => {
        const isActive = button.dataset.studioView === this.view;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      PIECES.forEach(piece => {
        const colourKey = this.selections[piece];
        const colour = this.getColour(colourKey);
        const image = this.getImage(piece, colourKey);

        const stagePiece = this.root.querySelector(`[data-stage-piece="${piece}"]`);
        if (stagePiece) {
          stagePiece.classList.toggle('is-active', piece === this.activePiece);
          stagePiece.setAttribute('aria-pressed', piece === this.activePiece ? 'true' : 'false');
          stagePiece.setAttribute('aria-label', `Edit ${this.getPieceTitle(piece)}, currently ${colour.label}`);
          const stageImage = stagePiece.querySelector('[data-stage-image]');
          const stageLabel = stagePiece.querySelector('[data-stage-label]');
          if (stageImage) {
            stageImage.src = image;
            stageImage.alt = `${colour.label} ${this.productName} ${piece === 'table' ? 'table' : 'chair'}`;
          }
          if (stageLabel) stageLabel.textContent = colour.label;
        }

        const detailImage = this.root.querySelector(`[data-detail-image="${piece}"]`);
        const detailLabel = this.root.querySelector(`[data-detail-label="${piece}"]`);
        if (detailImage) {
          detailImage.src = image;
          detailImage.alt = `Closer look at the ${colour.label} ${this.productName} ${piece === 'table' ? 'table' : 'chair'}`;
        }
        if (detailLabel) {
          detailLabel.textContent = `${colour.label} ${piece === 'table' ? 'Table' : 'Chair'}`;
        }

        const pieceButton = this.root.querySelector(`[data-studio-piece="${piece}"]`);
        if (pieceButton) {
          const isActive = piece === this.activePiece;
          pieceButton.classList.toggle('is-active', isActive);
          pieceButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          const thumb = pieceButton.querySelector('[data-piece-thumb]');
          const name = pieceButton.querySelector('[data-piece-colour]');
          if (thumb) {
            thumb.src = image;
            thumb.alt = '';
          }
          if (name) name.textContent = colour.label;
        }
      });

      if (this.controlHeading) {
        this.controlHeading.textContent = `Choose ${this.getPieceLabel(this.activePiece)} ${colourWord}`;
      }

      this.colourButtons.forEach(button => {
        const colourKey = button.dataset.studioColour;
        const isSelected = colourKey === this.selections[this.activePiece];
        const paletteImage = button.querySelector('[data-palette-image]');
        const availability = button.querySelector('[data-availability]');

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        if (paletteImage) {
          paletteImage.src = this.getImage(this.activePiece, colourKey);
          paletteImage.alt = '';
        }
        if (availability) {
          availability.hidden = !this.builderRoot || this.isAvailable(this.activePiece, colourKey);
        }
      });

      this.updateUseButton();
    }

    updateUseButton() {
      if (!this.useButton) return;
      const ready = Boolean(this.builderRoot);
      const pluralColourWord = this.root.dataset.colourWord === 'color' ? 'colors' : 'colours';
      const unavailablePieces = ready
        ? PIECES.filter(piece => !this.isAvailable(piece, this.selections[piece]))
        : PIECES;

      this.useButton.disabled = !ready || unavailablePieces.length > 0;
      if (!ready) {
        this.useButton.textContent = 'Loading choices';
      } else if (unavailablePieces.length) {
        this.useButton.textContent = `Choose available ${pluralColourWord}`;
      } else {
        this.useButton.textContent = this.useButton.dataset.defaultText || 'Add This Set to Cart';
      }
    }

    addCombinationToCart() {
      if (!this.builderRoot) return;
      const choices = PIECES.map(piece => this.getBuilderChoice(piece, this.selections[piece], true));
      if (choices.some(choice => !choice)) {
        const pluralColourWord = this.root.dataset.colourWord === 'color' ? 'colors' : 'colours';
        this.announce(`One or more selected ${pluralColourWord} are currently unavailable`);
        this.updateUseButton();
        return;
      }

      choices.forEach(choice => choice.click());
      const builder = this.builderRoot._jbBundle;
      if (!builder || typeof builder.addToCart !== 'function') {
        this.announce('The set could not be added here. Please use the purchasing builder above.');
        return;
      }

      this.useButton.disabled = true;
      this.useButton.textContent = 'Adding Set to Cart';
      this.announce('Adding your combination to the cart');
      Promise.resolve(builder.addToCart()).catch(() => {
        this.useButton.disabled = false;
        this.useButton.textContent = this.useButton.dataset.defaultText || 'Add This Set to Cart';
        this.announce('The set could not be added. Please try again.');
      });
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }
  }

  function initStudios() {
    document.querySelectorAll('[data-confetti-colour-studio]').forEach(root => {
      if (!root._jbConfettiColourStudio) {
        root._jbConfettiColourStudio = new JBConfettiColourStudio(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStudios);
  } else {
    initStudios();
  }

  document.addEventListener('shopify:section:load', initStudios);
})();
